import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import "./EventCalendar.css";

const kmToMiles = (km) => km * 0.621371;

function calculateDistance(lat1, lon1, lat2, lon2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function TodaysEventSidebar({ events, currentDate, setCurrentDate }) {
  const formattedDate = currentDate.toISOString().split("T")[0];

  const todaysEvents = events.filter((e) => {
    const start = e.extendedProps.startDate.split("T")[0];
    const end = e.extendedProps.endDate.split("T")[0];
    return formattedDate >= start && formattedDate <= end;
  });

  const handlePreviousDay = () => {
    const prevDate = new Date(currentDate);
    prevDate.setDate(prevDate.getDate() - 1);
    setCurrentDate(prevDate);
  };

  const handleNextDay = () => {
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + 1);
    setCurrentDate(nextDate);
  };

  return (
    <div className="today-sidebar">
      <div className="d-flex justify-content-between align-items-center mb-4 fs-2">
        <button className="btn btn-md-lg btn-outline-success" onClick={handlePreviousDay}>
          ◀
        </button>
        <h3 className="mb-0">{currentDate.toDateString()}</h3>
        <button className="btn btn-md-lg btn-outline-success" onClick={handleNextDay}>
          ▶
        </button>
      </div>
      <ul className="list-group">
        {todaysEvents.length === 0 ? (
          <li className="list-group-item text-muted">No events</li>
        ) : (
          todaysEvents.map((e, i) => (
            <li key={i} className="list-group-item">
              <Link to={`/rsvp/${e.extendedProps.slug}`} target="_blank">
                <strong>{e.extendedProps.actualTitle}</strong>
                <br />
                {e.extendedProps.location && (
                  <small>📍 {e.extendedProps.location}</small>
                )}
                <br />
                {e.extendedProps.virtualLink && (
                  <span className="badge bg-success">Virtual</span>
                )}
                <br />
                {e.extendedProps.leaderName &&
                e.extendedProps.leaderName === "Other Events" ? (
                  ""
                ) : (
                  <small>👤 {e.extendedProps.leaderName}</small>
                )}
              </Link>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

const formatPlusOneDate = (isoDateStr) => {
  const date = new Date(isoDateStr);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split("T")[0];
};

function EventCalendar({ location }) {
  const [events, setEvents] = useState([]);
  const [titles, setTitles] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [locations, setLocations] = useState([]); // NEW: store locations
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchParams, setSearchParams] = useSearchParams();
  const calendarRef = useRef(null);
  const [currentSidebarDate, setCurrentSidebarDate] = useState(new Date());

  const searchQuery = searchParams.get("q") || "";
  const title = searchParams.get("title") || "";
  const leader = searchParams.get("leader") || "";
  const locationFilter = searchParams.get("location") || "";
  const distance = searchParams.get("distance") || "";

  const colorPalette = [
    "#4CAF50", "#2196F3", "#FF9800", "#9C27B0", "#F44336",
    "#00BCD4", "#CDDC39", "#795548", "#FFEB3B", "#3F51B5",
    "#E91E63", "#8BC34A", "#009688", "#FFC107", "#673AB7",
    "#00ACC1", "#C2185B", "#607D8B", "#FFA726", "#00695C",
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, leaderRes, orgRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_URLVAR}/api/event/`),
          axios.get(`${process.env.REACT_APP_URLVAR}/api/leaders/home`),
          axios.get(`${process.env.REACT_APP_URLVAR}/api/organization/`),
        ]);
        const getDurationInDays = (start, end) => {
          const startDate = new Date(start);
          const endDate = new Date(end);
          const diffTime = endDate.getTime() - startDate.getTime();
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        };
        let colorIndex = 0;
        console.log(eventRes.data.events);
        const enriched = eventRes.data.events
          .map((e) => {
            const leaderObj = leaderRes.data.find((l) => l._id === e.leaderId);
            const orgObj = orgRes.data.find((o) => o._id === e.leaderId);
            const name = leaderObj?.name || orgObj?.name;
            if (!name) return null;

            const lat = leaderObj?.lat || orgObj?.lat;
            const lng = leaderObj?.lng || orgObj?.lng;

            const distanceKm =
              location?.latitude && location?.longitude && lat && lng
                ? calculateDistance(
                    location.latitude,
                    location.longitude,
                    parseFloat(lat),
                    parseFloat(lng)
                  )
                : null;
            const eventColor = colorPalette[colorIndex % colorPalette.length];
            colorIndex++;
            return {
              title: "",
              start: e.dateTime.split("T")[0],
              end: formatPlusOneDate(e.enddateTime),
              allDay: true,
              backgroundColor: eventColor,
              priority: getDurationInDays(e.dateTime, e.enddateTime),
              borderColor: "transparent",
              date: e.dateTime.split("T")[0],
              extendedProps: {
                actualTitle: e.title || "Untitled Event",
                location: e.location || "",
                slug: e.slug,
                virtualLink: e.virtualLink,
                leaderName: name,
                distance: distanceKm ? kmToMiles(distanceKm) : null,
                startDate: e.dateTime,
                endDate: e.enddateTime,
              },
            };
          })
          .filter(Boolean);

        setEvents(enriched);
        setTitles([...new Set(enriched.map((e) => e.extendedProps.actualTitle))]);
        setLeaders(
          [...new Set(enriched.map((e) => e.extendedProps.leaderName))].sort()
        );
        setLocations(
          [...new Set(enriched.map((e) => e.extendedProps.location).filter(Boolean))].sort()
        ); // NEW: set unique locations
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [location]);

  const filtered = events.filter((e) => {
    return (
      (title ? e.extendedProps.actualTitle === title : true) &&
      (leader ? e.extendedProps.leaderName === leader : true) &&
      (locationFilter ? e.extendedProps.location === locationFilter : true) && // NEW: filter by location
      (distance
        ? !e.extendedProps.virtualLink &&
          e.extendedProps.distance !== null &&
          e.extendedProps.distance <= parseFloat(distance)
        : true) &&
      (searchQuery
        ? e.extendedProps.actualTitle
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        : true)
    );
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <div className="spinner-border text-success" role="status"></div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <h3 className="text-center mb-4" style={{ color: "#4CAF50" }}>
        Event Calendar
      </h3>

      {/* Filters */}
      <div className="row g-2 mb-4">
        <div className="col-md-3">
          <input
            type="text"
            placeholder="🔍 Search title"
            className="form-control"
            value={searchQuery}
            onChange={(e) =>
              setSearchParams((prev) => {
                prev.set("q", e.target.value);
                return prev;
              })
            }
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={leader}
            onChange={(e) =>
              setSearchParams((prev) => {
                prev.set("leader", e.target.value);
                return prev;
              })
            }
          >
            <option value="">Filter by Leader</option>
            {leaders.map((l, i) => (
              <option key={i} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={title}
            onChange={(e) =>
              setSearchParams((prev) => {
                prev.set("title", e.target.value);
                return prev;
              })
            }
          >
            <option value="">Filter by Title</option>
            {titles.map((t, i) => (
              <option key={i} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={locationFilter}
            onChange={(e) =>
              setSearchParams((prev) => {
                prev.set("location", e.target.value);
                return prev;
              })
            }
          >
            <option value="">Filter by Location</option>
            {locations.map((loc, i) => (
              <option key={i} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 d-grid mt-2">
          <button
            className="btn btn-outline-success"
            onClick={() => setSearchParams({})}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {isMobile ? (
        <TodaysEventSidebar
          events={filtered}
          currentDate={currentSidebarDate}
          setCurrentDate={setCurrentSidebarDate}
        />
      ) : (
        <div className="row">
          <div className="col-lg-8">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              eventOrder="priority"
              initialView="dayGridMonth"
              events={filtered}
              eventDisplay="block"
              eventDidMount={({ el, event }) => {
                const tooltip = document.createElement("div");
                tooltip.innerHTML = `
                  <strong>${event.extendedProps.actualTitle}</strong><br />
                  ${
                    event.extendedProps.location
                      ? `📍 ${event.extendedProps.location}<br/>`
                      : ""
                  }
                  🗓 ${event.extendedProps.startDate.split("T")[0]} - ${
                  event.extendedProps.endDate.split("T")[0]
                }
                `;
                tooltip.className = "custom-tooltip";
                el.setAttribute("title", tooltip.innerText);
              }}
              eventClick={({ event }) => {
                const slug = event.extendedProps.slug;
                if (slug) {
                  window.open(`/rsvp/${slug}`, "_blank");
                }
              }}
              displayEventEnd={true}
              displayEventTime={false}
              height="auto"
            />
          </div>
          <div className="col-lg-4">
            <TodaysEventSidebar
              events={filtered}
              currentDate={currentSidebarDate}
              setCurrentDate={setCurrentSidebarDate}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default EventCalendar;
